import React, { useEffect, useMemo, useState } from "react";
import { Clock, Eye, RefreshCw } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import API from "../../../util/API";
import { normalizeCurrencyRates } from "../../../util/money";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const toFlagClass = (code) => {
  const raw = String(code || "").trim().toLowerCase();
  if (raw.length !== 2) return "";
  return `fi fi-${raw}`;
};

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const SelectBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const Chip = ({ children, tone = "slate" }) => {
  const toneMap = {
    slate: "border-slate-200 bg-slate-50 text-slate-800",
    white: "border-slate-200 bg-white text-slate-700",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-black",
        toneMap[tone] || toneMap.slate
      )}
    >
      {children}
    </span>
  );
};

const ADDRESS_FIELD_OPTIONS = [
  { key: "fullName", label: "Full Name" },
  { key: "phoneCode", label: "Phone Code" },
  { key: "phoneNumber", label: "Phone Number" },
  { key: "addressLine1", label: "Address Line 1" },
  { key: "addressLine2", label: "Address Line 2 (Optional)" },
  { key: "city", label: "City" },
  { key: "stateProvince", label: "State/Province" },
  { key: "postalCode", label: "Zip/Postal Code" },
];

export default function CountryCurrencyManagement() {
  const [currencyPayload, setCurrencyPayload] = useState(null);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [currencySyncing, setCurrencySyncing] = useState(false);
  const [currencyError, setCurrencyError] = useState(null);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [autoSyncHours, setAutoSyncHours] = useState(0);
  const [nextAutoSyncAt, setNextAutoSyncAt] = useState(null);

  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [countryLoading, setCountryLoading] = useState(false);
  const [countrySaving, setCountrySaving] = useState(false);
  const [countryError, setCountryError] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [countryDraftEnabled, setCountryDraftEnabled] = useState([]);

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressCountries, setAddressCountries] = useState([]);
  const [addressCountryCode, setAddressCountryCode] = useState("");
  const [addressFieldsDraft, setAddressFieldsDraft] = useState([]);
  const [addressFieldsLoading, setAddressFieldsLoading] = useState(false);
  const [addressFieldsSaving, setAddressFieldsSaving] = useState(false);
  const [addressFieldsError, setAddressFieldsError] = useState(null);
  const [addressCountryMeta, setAddressCountryMeta] = useState({
    countryName: "",
    countryCode: "",
    phoneCode: "",
    flagEmoji: "",
  });

  const currencyRatesByCode = useMemo(() => normalizeCurrencyRates(currencyPayload), [currencyPayload]);

  const currencyMeta = useMemo(() => {
    const updatedAtRaw = currencyPayload?.updated_at;
    const updatedAt = updatedAtRaw ? new Date(updatedAtRaw) : null;
    const source = currencyPayload?.source || "—";

    const codes = Object.keys(currencyRatesByCode || {});
    const count = codes.length;
    const sortedCodes = codes.sort((a, b) => a.localeCompare(b));

    const countryCount = currencyPayload?.rates && typeof currencyPayload.rates === "object"
      ? Object.keys(currencyPayload.rates).length
      : 0;

    return { updatedAt, updatedAtRaw, source, count, sortedCodes, countryCount };
  }, [currencyPayload, currencyRatesByCode]);

  const countryDraftSet = useMemo(
    () => new Set(countryDraftEnabled.map((c) => String(c || "").toUpperCase())),
    [countryDraftEnabled]
  );

  const filteredCountryList = useMemo(() => {
    const q = String(countrySearch || "").trim().toLowerCase();
    if (!q) return countryList;
    return (countryList || []).filter((c) => {
      const name = String(c?.countryName || "").toLowerCase();
      const code = String(c?.countryCode || "").toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [countrySearch, countryList]);

  const addressFieldDraftSet = useMemo(
    () => new Set(addressFieldsDraft.map((f) => String(f || ""))),
    [addressFieldsDraft]
  );


  const fetchCurrency = async () => {
    setCurrencyError(null);
    setCurrencyLoading(true);
    try {
      const res = await API.get("/settings/currency");
      setCurrencyPayload(res.data || null);
    } catch (e) {
      setCurrencyError(e?.response?.data?.message || e?.message || "Failed to load currency rates");
      setCurrencyPayload(null);
    } finally {
      setCurrencyLoading(false);
    }
  };

  const fetchSyncConfig = async () => {
    try {
      const res = await API.get("/settings/currency/sync-config");
      const enabled = Boolean(res.data?.enabled);
      const intervalHours = Number(res.data?.intervalHours);
      setAutoSyncHours(enabled ? intervalHours : 0);
    } catch (e) {
      setCurrencyError(e?.response?.data?.message || e?.message || "Failed to load auto sync config");
    }
  };

  const updateSyncConfig = async (hours) => {
    const intervalHours = Number(hours);
    const enabled = Boolean(intervalHours);
    setAutoSyncHours(enabled ? intervalHours : 0);

    try {
      await API.put("/settings/currency/sync-config", { enabled, intervalHours });
    } catch (e) {
      setCurrencyError(e?.response?.data?.message || e?.message || "Failed to update auto sync config");
    }
  };

  const syncCurrencyNow = async () => {
    setCurrencyError(null);
    setCurrencySyncing(true);
    try {
      await API.post("/settings/currency/refresh");
      await fetchCurrency();
    } catch (e) {
      setCurrencyError(e?.response?.data?.message || e?.message || "Failed to sync currency rates");
    } finally {
      setCurrencySyncing(false);
    }
  };

  const fetchAllCountries = async () => {
    setCountryError(null);
    setCountryLoading(true);
    try {
      const res = await API.get("/settings/countries/all");
      const list = Array.isArray(res.data?.countries) ? res.data.countries : [];
      setCountryList(list);
      const enabled = list.filter((c) => c?.isEnabled).map((c) => c.countryCode);
      setCountryDraftEnabled(enabled);
    } catch (e) {
      setCountryError(e?.response?.data?.message || e?.message || "Failed to load countries");
      setCountryList([]);
    } finally {
      setCountryLoading(false);
    }
  };

  const toggleCountryEnabled = (code) => {
    const upper = String(code || "").toUpperCase();
    setCountryDraftEnabled((prev) => {
      const set = new Set(prev.map((c) => String(c || "").toUpperCase()));
      if (set.has(upper)) set.delete(upper);
      else set.add(upper);
      return Array.from(set);
    });
  };

  const saveEnabledCountries = async () => {
    setCountryError(null);
    setCountrySaving(true);
    try {
      await API.put("/settings/countries", { enabledCountries: countryDraftEnabled });
      await fetchAllCountries();
      setCountryModalOpen(false);
    } catch (e) {
      setCountryError(e?.response?.data?.message || e?.message || "Failed to save countries");
    } finally {
      setCountrySaving(false);
    }
  };

  const openCountryModal = async () => {
    await fetchAllCountries();
    setCountrySearch("");
    setCountryModalOpen(true);
  };

  const fetchAddressCountries = async () => {
    setAddressFieldsError(null);
    try {
      const res = await API.get("/settings/countries");
      const list = Array.isArray(res.data?.countries) ? res.data.countries : [];
      setAddressCountries(list);
      return list;
    } catch (e) {
      setAddressFieldsError(e?.response?.data?.message || e?.message || "Failed to load enabled countries");
      setAddressCountries([]);
      return [];
    }
  };

  const fetchAddressFields = async (code) => {
    if (!code) return;
    setAddressFieldsError(null);
    setAddressFieldsLoading(true);
    try {
      const res = await API.get("/settings/country-address-fields", {
        params: { country: code },
      });
      const fields = Array.isArray(res.data?.addressFields) ? res.data.addressFields : [];
      setAddressFieldsDraft(fields);
      setAddressCountryMeta({
        countryName: res.data?.countryName || "",
        countryCode: res.data?.countryCode || code,
        phoneCode: res.data?.phoneCode || "",
        flagEmoji: res.data?.flagEmoji || "",
      });
    } catch (e) {
      setAddressFieldsError(e?.response?.data?.message || e?.message || "Failed to load address fields");
      setAddressFieldsDraft([]);
    } finally {
      setAddressFieldsLoading(false);
    }
  };

  const openAddressModal = async () => {
    const list = await fetchAddressCountries();
    const first = list?.[0]?.countryCode || "";
    const selected = addressCountryCode || first;
    setAddressCountryCode(selected);
    if (selected) await fetchAddressFields(selected);
    setAddressModalOpen(true);
  };

  const toggleAddressField = (key) => {
    setAddressFieldsDraft((prev) => {
      const set = new Set(prev.map((f) => String(f || "")));
      if (set.has(key)) set.delete(key);
      else set.add(key);
      if (set.has("addressLine1")) set.add("addressLine2");
      return Array.from(set);
    });
  };

  const saveAddressFields = async () => {
    if (!addressCountryCode) return;
    setAddressFieldsError(null);
    setAddressFieldsSaving(true);
    try {
      const res = await API.put("/settings/country-address-fields", {
        countryCode: addressCountryCode,
        addressFields: addressFieldsDraft,
      });
      setAddressFieldsDraft(Array.isArray(res.data?.addressFields) ? res.data.addressFields : addressFieldsDraft);
      setAddressModalOpen(false);
    } catch (e) {
      setAddressFieldsError(e?.response?.data?.message || e?.message || "Failed to save address fields");
    } finally {
      setAddressFieldsSaving(false);
    }
  };

  useEffect(() => {
    fetchCurrency();
    fetchSyncConfig();
  }, []);

  useEffect(() => {
    if (!autoSyncHours) {
      setNextAutoSyncAt(null);
      return;
    }

    const intervalMs = autoSyncHours * 60 * 60 * 1000;
    const computeNext = () => {
      const last = currencyMeta.updatedAt ? currencyMeta.updatedAt.getTime() : 0;
      const base = last > 0 ? last : Date.now();
      return new Date(base + intervalMs);
    };

    setNextAutoSyncAt(computeNext());
  }, [autoSyncHours, currencyMeta.updatedAtRaw]);

  const formatRate = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return "—";
    if (n >= 100) return n.toFixed(2);
    if (n >= 1) return n.toFixed(4);
    return n.toFixed(6);
  };

  return (
    <AdminPageShell
      title="Country & Currency Management"
      description="Sync currency rates and manage country availability across the storefront."
      actions={
        <button
          type="button"
          onClick={() => {
            fetchCurrency();
            fetchSyncConfig();
          }}
          className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors"
        >
          <RefreshCw size={16} className={currencyLoading ? "animate-spin" : ""} />
          Refresh
        </button>
      }
    >
      <div className="grid gap-6">
        <Card className="p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
                <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">
                  Country & Address Field
                </h2>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                Control which address fields are shown per country in the storefront profile.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <SecondaryButton type="button" onClick={openCountryModal}>
                Country
              </SecondaryButton>
              <PrimaryButton type="button" onClick={openAddressModal}>
                Address Fields
              </PrimaryButton>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
              <RefreshCw className="h-4 w-4 text-slate-700" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Currency Sync</h2>
              <Chip tone="white">Base: USD</Chip>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <SecondaryButton
                type="button"
                onClick={() => setShowCurrencyModal(true)}
                disabled={currencyLoading || !currencyMeta.count}
              >
                <Eye className="h-4 w-4" />
                View
              </SecondaryButton>

              <PrimaryButton
                type="button"
                onClick={syncCurrencyNow}
                disabled={currencySyncing}
              >
                <RefreshCw className={cx("h-4 w-4", currencySyncing ? "animate-spin" : "")} />
                Sync Now
              </PrimaryButton>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Last Updated</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {currencyLoading ? "Loading…" : (currencyMeta.updatedAt ? currencyMeta.updatedAt.toLocaleString() : "—")}
              </p>
              <p className="mt-1 text-xs text-slate-600">Source: {currencyMeta.source}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Rates Available</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {currencyLoading
                  ? "Loading…"
                  : `${currencyMeta.countryCount || 0} countries / ${currencyMeta.count || 0} currencies`}
              </p>
              <p className="mt-1 text-xs text-slate-600">View shows: 1 USD = target currency</p>
              <p className="mt-1 text-xs text-slate-500">Sync uses only enabled countries.</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Auto Sync</p>
                  <p className="mt-1 text-xs text-slate-600">Automatically refresh currency rates.</p>
                </div>
                <Clock className="h-4 w-4 text-slate-700" />
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <select
                  value={autoSyncHours}
                  onChange={(e) => updateSyncConfig(Number(e.target.value))}
                  className={SelectBase}
                >
                  <option value={0}>Off</option>
                  <option value={1}>Every 1 hour</option>
                  <option value={3}>Every 3 hours</option>
                  <option value={6}>Every 6 hours</option>
                  <option value={12}>Every 12 hours</option>
                  <option value={24}>Every 24 hours</option>
                </select>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2">
                  <p className="text-xs text-slate-500">Next sync</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {autoSyncHours ? (nextAutoSyncAt ? nextAutoSyncAt.toLocaleString() : "—") : "Disabled"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {currencyError ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              {currencyError}
            </div>
          ) : null}
        </Card>

      </div>

      {showCurrencyModal ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setShowCurrencyModal(false)}
            role="button"
            tabIndex={-1}
            aria-label="Close"
          />

          <div className="relative w-full max-w-3xl rounded-2xl bg-white border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="text-sm font-black tracking-tight text-slate-900">Currency Rates</p>
                <p className="text-xs text-slate-600">Base currency: USD</p>
              </div>
              <SecondaryButton type="button" onClick={() => setShowCurrencyModal(false)} className="px-4">
                Close
              </SecondaryButton>
            </div>

            <div className="max-h-[70vh] overflow-auto">
              <div className="px-5 py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Base</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">USD</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Updated</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {currencyMeta.updatedAt ? currencyMeta.updatedAt.toLocaleString() : "—"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">Currencies</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{currencyMeta.count || 0}</p>
                  </div>
                </div>

                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Currency</th>
                        <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Rate</th>
                        <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currencyMeta.sortedCodes.length ? (
                        currencyMeta.sortedCodes.map((code) => {
                          const rate = currencyRatesByCode[code];
                          return (
                            <tr key={code} className="border-t border-slate-200">
                              <td className="px-4 py-3 text-sm font-semibold text-slate-900">{code}</td>
                              <td className="px-4 py-3 text-sm text-slate-700">{formatRate(rate)}</td>
                              <td className="px-4 py-3 text-sm text-slate-700">1 USD = {formatRate(rate)} {code}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td className="px-4 py-6 text-sm text-slate-700" colSpan={3}>
                            No currency rates found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {countryModalOpen ? (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setCountryModalOpen(false)}
            role="button"
            tabIndex={-1}
            aria-label="Close"
          />

          <div className="relative w-full max-w-4xl rounded-2xl bg-white border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="text-sm font-black tracking-tight text-slate-900">Country Management</p>
                <p className="text-xs text-slate-600">Select countries to enable site-wide.</p>
              </div>
              <SecondaryButton type="button" onClick={() => setCountryModalOpen(false)} className="px-4">
                Close
              </SecondaryButton>
            </div>

            <div className="px-5 py-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-slate-600">
                  Selected: {countryDraftSet.size} / {countryList.length}
                </div>
                <div className="min-w-[240px]">
                  <input
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    placeholder="Search country..."
                    className={FieldBase}
                  />
                </div>
              </div>

              {countryError ? (
                <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                  {countryError}
                </div>
              ) : null}

              <div className="mt-4 max-h-[55vh] overflow-auto rounded-2xl border border-slate-200 p-3">
                {countryLoading ? (
                  <div className="text-sm text-slate-600">Loading countries...</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {filteredCountryList.map((c) => {
                      const checked = countryDraftSet.has(String(c?.countryCode || "").toUpperCase());
                      return (
                        <label
                          key={c.countryCode}
                          className={cx(
                            "flex items-center gap-3 rounded-2xl border px-3 py-2 cursor-pointer",
                            checked ? "border-slate-300 bg-slate-50" : "border-slate-200 bg-white"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCountryEnabled(c.countryCode)}
                            className="h-4 w-4"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                              {toFlagClass(c.countryCode) ? (
                                <span
                                  className={cx("inline-block rounded-sm", toFlagClass(c.countryCode))}
                                  aria-hidden="true"
                                />
                              ) : null}
                              <span>{c.countryName}</span>
                            </p>
                            <p className="text-xs text-slate-600">
                              {c.countryCode} {c.phoneCode ? `• ${c.phoneCode}` : ""}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <SecondaryButton type="button" onClick={() => setCountryModalOpen(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={saveEnabledCountries} disabled={countrySaving}>
                <RefreshCw className={cx("h-4 w-4", countrySaving ? "animate-spin" : "")} />
                Save
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}

      {addressModalOpen ? (
        <div className="fixed inset-0 z-[140] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setAddressModalOpen(false)}
            role="button"
            tabIndex={-1}
            aria-label="Close"
          />

          <div className="relative w-full max-w-4xl rounded-2xl bg-white border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="text-sm font-black tracking-tight text-slate-900">Country Address Fields</p>
                <p className="text-xs text-slate-600">Select visible address fields per country.</p>
              </div>
              <SecondaryButton type="button" onClick={() => setAddressModalOpen(false)} className="px-4">
                Close
              </SecondaryButton>
            </div>

            <div className="px-5 py-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="min-w-[240px]">
                  <label className="text-xs font-semibold text-slate-600">Country</label>
                  <select
                    value={addressCountryCode}
                    onChange={async (e) => {
                      const next = e.target.value;
                      setAddressCountryCode(next);
                      await fetchAddressFields(next);
                    }}
                    className={SelectBase}
                  >
                    {addressCountries.map((c) => (
                      <option key={c.countryCode} value={c.countryCode}>
                        {c.countryName || c.countryCode}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="text-xs text-slate-600">
                  Selected fields: {addressFieldDraftSet.size}
                </div>
              </div>

              {addressFieldsError ? (
                <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                  {addressFieldsError}
                </div>
              ) : null}

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                {ADDRESS_FIELD_OPTIONS.map((field) => {
                  const checked = addressFieldDraftSet.has(field.key);
                  return (
                    <label
                      key={field.key}
                      className={cx(
                        "flex items-center gap-3 rounded-2xl border px-3 py-2 cursor-pointer",
                        checked ? "border-slate-300 bg-slate-50" : "border-slate-200 bg-white"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAddressField(field.key)}
                        className="h-4 w-4"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{field.label}</p>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Selected Country Preview</p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Country Name</p>
                    <p className="font-semibold text-slate-900 flex items-center gap-2">
                      {toFlagClass(addressCountryMeta.countryCode || addressCountryCode) ? (
                        <span
                          className={cx(
                            "inline-block rounded-sm",
                            toFlagClass(addressCountryMeta.countryCode || addressCountryCode)
                          )}
                          aria-hidden="true"
                        />
                      ) : null}
                      <span>{addressCountryMeta.countryName || "—"}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Country Code</p>
                    <p className="font-semibold text-slate-900">
                      {addressCountryMeta.countryCode || addressCountryCode || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Phone Code</p>
                    <p className="font-semibold text-slate-900">
                      {addressCountryMeta.phoneCode || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <SecondaryButton type="button" onClick={() => setAddressModalOpen(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={saveAddressFields} disabled={addressFieldsSaving || addressFieldsLoading}>
                <RefreshCw className={cx("h-4 w-4", addressFieldsSaving ? "animate-spin" : "")} />
                Save
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
